import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useI18n, FAMILIES } from "@/lib/i18n";

export default function NewInterviewDialog({ audit, open, onOpenChange, onDone }) {
  const { tx, lbl } = useI18n();
  const nav = useNavigate();
  const [emps, setEmps] = useState([]);
  const [members, setMembers] = useState([]);
  const [f, setF] = useState({ mode: "one", employee: "", family: "", consultant_id: "", scheduled_at: "" });
  useEffect(() => {
    api.get(`/audits/${audit.id}/employees`, { params: { without_interview: true, page_size: 500 } }).then((r) => setEmps(r.data.items)).catch(() => {});
    api.get(`/audits/${audit.id}/members`).then((r) => setMembers(r.data.filter((m) => !m.revoked_at && ["consultant", "lead"].includes(m.role)))).catch(() => {});
  }, [audit.id]);
  const emp = emps.find((e) => e.id === f.employee);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const go = async () => {
    try {
      const { data } = await api.post(`/audits/${audit.id}/interviews`, {
        employee_refs: f.mode === "one" ? [f.employee] : [], all_without_interview: f.mode === "all",
        question_set_family: f.family || null, consultant_id: f.consultant_id || null,
        scheduled_at: f.scheduled_at || null,
      });
      toast.success(`${data.created} ${tx("fiche(s) d'entretien créée(s)", "interview sheet(s) created")}`);
      onOpenChange(false); onDone();
      if (f.mode === "one" && data.ids[0]) nav(`/interviews/${data.ids[0]}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-interview-dialog">
        <DialogHeader><DialogTitle className="text-[#153A63]">{tx("Nouvel entretien", "New interview")}</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          {[["one", tx("Un collaborateur", "One employee")], ["all", tx(`Tous sans fiche (${emps.length})`, `All without a sheet (${emps.length})`)]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`new-iv-mode-${k}`} onClick={() => setF({ ...f, mode: k })} className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${f.mode === k ? "border-[#2176AE] bg-[#EAF3FA] text-[#153A63] font-medium" : "border-slate-200 hover:border-slate-300"}`}>{l}</button>
          ))}
        </div>
        {f.mode === "one" && <Field label={tx("Collaborateur", "Employee")}>
          <select data-testid="new-iv-employee" className="nselect w-full" value={f.employee} onChange={set("employee")}>
            <option value="">—</option>
            {emps.map((e) => <option key={e.id} value={e.id}>{e.employee_id} · {e.first_name} {e.last_name} — {e.job_title || e.unit}</option>)}
          </select>
          {emps.length === 0 && <p className="text-xs text-slate-500 mt-1">{tx("Tous les collaborateurs ont déjà une fiche.", "Every employee already has a sheet.")}</p>}
        </Field>}
        <Field label={tx("Questionnaire (trame)", "Questionnaire")}>
          <select data-testid="new-iv-family" className="nselect w-full" value={f.family} onChange={set("family")}>
            <option value="">{tx("Proposé selon l'ancienneté", "Proposed by tenure")}{emp ? ` : ${lbl(FAMILIES, emp.questionnaire_family)}` : ""}</option>
            {Object.keys(FAMILIES).map((k) => <option key={k} value={k}>{lbl(FAMILIES, k)}</option>)}
          </select>
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={tx("Auditeur", "Auditor")}><select data-testid="new-iv-consultant" className="nselect w-full" value={f.consultant_id} onChange={set("consultant_id")}><option value="">{tx("À attribuer plus tard", "Assign later")}</option>{members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}</select></Field>
          <Field label={tx("Date et heure", "Date & time")}><Input type="datetime-local" data-testid="new-iv-date" value={f.scheduled_at} onChange={set("scheduled_at")} /></Field>
        </div>
        <DialogFooter><Button data-testid="new-iv-submit" disabled={(f.mode === "one" && !f.employee) || (f.mode === "all" && !emps.length)} onClick={go} className="bg-[#153A63]">{tx("Créer la fiche d'entretien", "Create interview sheet")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
